
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from "@/components/ui/button";
import { AppError } from './types';
import logger from '@/utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('Erreur capturée par ErrorBoundary:', error);
    logger.error('Stack trace:', errorInfo.componentStack);
    
    this.setState({ errorInfo });
    
    // Log l'erreur vers un service externe si nécessaire
    this.logError(error, errorInfo);
  }

  private logError(error: Error, errorInfo: ErrorInfo) {
    // Implémentation future pour envoyer les erreurs à un service de monitoring
    logger.log('Erreur à logger:', {
      error,
      errorInfo,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Oups ! Quelque chose s'est mal passé</h1>
            <p className="text-gray-600 mb-4">
              Nous sommes désolés, mais une erreur inattendue s'est produite. 
              Veuillez rafraîchir la page ou revenir à l'accueil.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-4 p-3 bg-gray-100 rounded overflow-auto text-sm">
                <p className="font-bold text-red-600">{this.state.error.toString()}</p>
                {this.state.errorInfo && (
                  <pre className="mt-2 text-xs text-gray-700">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
            <div className="flex gap-4">
              <Button
                onClick={() => window.location.reload()}
                className="bg-blue-500 hover:bg-blue-600"
              >
                Rafraîchir la page
              </Button>
              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorToast = ({ error }: { error: AppError }) => {
  // Using a useEffect hook directly in a component instead of with useToast
  React.useEffect(() => {
    logger.error('Error occurred:', error);
  }, [error]);

  return null;
};
